import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AnimatePresence } from 'framer-motion';
import { Toast, type ToastItem, type ToastVariant } from './Toast';

/**
 * ToastProvider — proveedor global de notificaciones (DESIGN-SYSTEM §3.10).
 *
 * Diseno:
 *  - Estado interno con array `ToastItem[]`. Cada toast tiene id unico (counter
 *    monotono, no Date.now para evitar colisiones en rafagas).
 *  - `useToast()` devuelve `{ toast, dismiss }`. `toast(input)` retorna el id
 *    para permitir cancelar manualmente.
 *  - Auto-dismiss configurable via `duration` (default 4000 ms). `duration: 0`
 *    o `null` = persistente.
 *  - Stack maximo de 3 visibles (DESIGN-SYSTEM §8 a11y). Los nuevos empujan los
 *    viejos al fondo. Si se excede, se descarta el mas antiguo.
 *  - Posicion: top-right (fixed). Contenedor con `aria-live` redundante a los
 *    role de cada toast individual; cada `Toast` decide `polite` vs `assertive`.
 */
export interface ToastInput {
  variant?: ToastVariant;
  title: string;
  description?: string;
  /** Duracion en ms. 0 o null = persistente. Default 4000. */
  duration?: number | null;
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  toast: (input: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION = 4000;
const MAX_VISIBLE = 3;

export interface ToastProviderProps {
  children: ReactNode;
  /** Override de duracion default. */
  defaultDuration?: number;
}

export function ToastProvider({
  children,
  defaultDuration = DEFAULT_DURATION,
}: ToastProviderProps) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const idCounter = useRef(0);
  // Map id -> timeout para poder cancelar al dismiss manual.
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const toast = useCallback(
    (input: ToastInput): number => {
      idCounter.current += 1;
      const id = idCounter.current;
      const variant: ToastVariant = input.variant ?? 'info';
      const duration =
        input.duration === null || input.duration === 0
          ? null
          : input.duration ?? defaultDuration;

      const item: ToastItem = {
        id,
        variant,
        title: input.title,
        description: input.description,
        action: input.action,
        duration,
      };

      setItems((prev) => {
        // Cap a MAX_VISIBLE descartando el mas antiguo (cabeza).
        const next = [...prev, item];
        if (next.length > MAX_VISIBLE) {
          const drop = next.shift();
          if (drop) {
            const timer = timers.current.get(drop.id);
            if (timer) {
              clearTimeout(timer);
              timers.current.delete(drop.id);
            }
          }
        }
        return next;
      });

      if (duration !== null) {
        const timer = setTimeout(() => dismiss(id), duration);
        timers.current.set(id, timer);
      }
      return id;
    },
    [defaultDuration, dismiss],
  );

  // Limpieza global al desmontar provider.
  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach((timer) => clearTimeout(timer));
      map.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Viewport top-right. role="region" + aria-label para que SR pueda saltar via F6 (DESIGN-SYSTEM §8.2). */}
      <div
        role="region"
        aria-label="Notificaciones"
        className="pointer-events-none fixed top-4 right-4 z-toast flex flex-col items-end gap-3 w-full max-w-sm"
      >
        <AnimatePresence initial={false}>
          {items.map((item) => (
            <Toast key={item.id} item={item} onDismiss={() => dismiss(item.id)} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

/** Hook publico para emitir toasts. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast() debe usarse dentro de <ToastProvider>.');
  }
  return ctx;
}
